/**
 * Dominant-colour extraction for the Sampled Palette Builder.
 *
 * The picture is drawn on a small canvas (≤ 96 px on the long side), near-white / near-black / very grey pixels are set aside,
 * and the remaining pixels are quantised with median-cut into boxes. Each box's average colour is a *candidate*; the
 * candidates are then arranged into several five-colour palettes ("suggestions") so the user can pick a mood:
 *   dominant — the colours that cover most of the picture
 *   vivid    — saturated colours first
 *   soft     — the lighter, calmer colours (pastel tints when the picture has none)
 *   contrast — the most different colours (light / dark / hue spread)
 */
import { hexToHsl, hslToHex, rgbToHex, shade, tint } from './colors';

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  if (!String(src).startsWith('data:')) img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('The picture could not be read.'));
  img.src = src;
});

const medianCut = (pixels, count) => {
  const boxes = [pixels];
  while (boxes.length < count) {
    // split the box with the largest colour range
    let bi = -1, bestRange = -1, bestCh = 0;
    boxes.forEach((box, i) => {
      if (box.length < 2) return;
      for (let ch = 0; ch < 3; ch += 1) {
        let lo = 255, hi = 0;
        for (const p of box) { if (p[ch] < lo) lo = p[ch]; if (p[ch] > hi) hi = p[ch]; }
        if (hi - lo > bestRange) { bestRange = hi - lo; bi = i; bestCh = ch; }
      }
    });
    if (bi < 0 || bestRange < 8) break;
    const box = boxes[bi].slice().sort((a, b) => a[bestCh] - b[bestCh]);
    const mid = Math.floor(box.length / 2);
    boxes.splice(bi, 1, box.slice(0, mid), box.slice(mid));
  }
  return boxes.filter((b) => b.length).map((box) => {
    let r = 0, g = 0, b = 0;
    for (const p of box) { r += p[0]; g += p[1]; b += p[2]; }
    const n = box.length;
    return { rgb: { r: Math.round(r / n), g: Math.round(g / n), b: Math.round(b / n) }, count: n };
  });
};

const dist = (a, b) => Math.abs(a.r - b.r) + Math.abs(a.g - b.g) + Math.abs(a.b - b.b);
const hexRgb = (hex) => ({ r: parseInt(hex.slice(1, 3), 16), g: parseInt(hex.slice(3, 5), 16), b: parseInt(hex.slice(5, 7), 16) });

/**
 * Colour candidates of `src`: `[{ hex, share, sat, light }]` sorted by share (share = 0..1 of the sampled pixels).
 * Up to ~12 distinct colours; empty when the picture has no usable colour.
 */
export const extractPaletteCandidates = async (src) => {
  const img = await loadImage(src);
  const max = 96;
  const s = Math.min(1, max / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
  const w = Math.max(1, Math.round((img.naturalWidth || img.width) * s)), h = Math.max(1, Math.round((img.naturalHeight || img.height) * s));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);
  const pixels = [], greys = [];
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 128) continue;
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    if (mx > 245 && mn > 235) continue; // paper white
    if (mx < 18) continue; // pure black
    if (mx - mn < 12) { greys.push([r, g, b]); continue; } // neutral greys are kept as a fallback only
    pixels.push([r, g, b]);
  }
  const source = pixels.length >= 16 ? pixels : pixels.concat(greys);
  if (!source.length) return [];
  const total = source.length;
  const boxes = medianCut(source, 16);
  // merge near-duplicates
  const merged = [];
  boxes.sort((a, b) => b.count - a.count).forEach((bx) => {
    const near = merged.find((m) => dist(m.rgb, bx.rgb) < 60);
    if (near) {
      const n = near.count + bx.count;
      near.rgb = { r: Math.round((near.rgb.r * near.count + bx.rgb.r * bx.count) / n), g: Math.round((near.rgb.g * near.count + bx.rgb.g * bx.count) / n), b: Math.round((near.rgb.b * near.count + bx.rgb.b * bx.count) / n) };
      near.count = n;
    } else merged.push({ ...bx });
  });
  return merged
    .map((m) => { const hex = rgbToHex(m.rgb); const { s: sat, l } = hexToHsl(hex); return { hex, share: m.count / total, sat, light: l }; })
    .sort((a, b) => b.share - a.share)
    .slice(0, 12);
};

/** Fill a palette up to `count` colours with tints / shades of what it already has (so every suggestion has five colours). */
const padPalette = (list, pool, count) => {
  const out = list.slice(0, count);
  const has = (hex) => out.some((c) => c.hex.toLowerCase() === hex.toLowerCase());
  for (const c of pool) { if (out.length >= count) break; if (!has(c.hex)) out.push(c); }
  let i = 0;
  while (out.length < count && out.length) {
    const base = out[i % out.length];
    const hex = i % 2 === 0 ? tint(base.hex, 0.35) : shade(base.hex, 0.3);
    if (!has(hex)) out.push({ hex, share: 0, sat: hexToHsl(hex).s, light: hexToHsl(hex).l, derived: true });
    i += 1;
    if (i > 20) break;
  }
  return out;
};

const vividScore = (c) => (c.sat / 100) * (1 - Math.abs(c.light - 50) / 50);

/**
 * Five-colour palette suggestions from the candidates: `[{ id, label, hint, colors: [{ hex, share }] }]`.
 * Suggestions that would repeat an earlier one are dropped, so a picture with few colours yields fewer cards.
 */
export const paletteVariants = (candidates, count = 5) => {
  if (!candidates || !candidates.length) return [];
  const byShare = candidates.slice().sort((a, b) => b.share - a.share);
  const out = [];
  const push = (id, label, hint, colors) => {
    const key = colors.map((c) => c.hex.toLowerCase()).join(',');
    if (out.some((v) => v.key === key)) return;
    out.push({ id, label, hint, key, colors: colors.map(({ hex, share }) => ({ hex, share })) });
  };

  // 1. dominant
  push('dominant', 'Dominant', 'the colours that cover most of the picture', padPalette(byShare, byShare, count));

  // 2. vivid — saturated first (weighted by how much of the picture they cover)
  const vivid = candidates.slice().sort((a, b) => b.share * (0.15 + vividScore(b)) - a.share * (0.15 + vividScore(a)));
  push('vivid', 'Vivid', 'saturated colours first — good for accents', padPalette(vivid, byShare, count));

  // 3. soft — light, calm colours; pastel tints of the dominant ones when the picture is dark / saturated
  const softPool = candidates.filter((c) => c.light >= 45 && c.light <= 85 && c.sat <= 55).sort((a, b) => b.share - a.share);
  const soft = softPool.length >= 3
    ? padPalette(softPool, byShare.map((c) => ({ ...c, hex: tint(c.hex, 0.45), derived: true })), count)
    : byShare.slice(0, count).map((c) => { const { h, s } = hexToHsl(c.hex); const hex = hslToHex({ h, s: Math.min(s, 45), l: 72 }); return { hex, share: c.share, sat: Math.min(s, 45), light: 72, derived: true }; });
  push('soft', 'Soft', 'lighter, calmer colours — good for backgrounds', padPalette(soft, byShare, count));

  // 4. contrast — greedy farthest-point selection: the most different colours (light / dark / hue spread)
  if (candidates.length >= 3) {
    const rgb = candidates.map((c) => ({ ...c, _rgb: hexRgb(c.hex) }));
    const picked = [rgb[0]];
    while (picked.length < Math.min(count, rgb.length)) {
      let best = null, bestD = -1;
      for (const c of rgb) {
        if (picked.includes(c)) continue;
        const d = Math.min(...picked.map((p) => dist(p._rgb, c._rgb))) * (0.6 + Math.min(c.share * 6, 0.6));
        if (d > bestD) { bestD = d; best = c; }
      }
      if (!best) break;
      picked.push(best);
    }
    push('contrast', 'Contrast', 'the most different colours of the picture', padPalette(picked, byShare, count));
  }
  return out.map(({ key, ...v }) => v);
};

/**
 * `count` dominant colours of `src` as `[{ hex, share }]`. mode: 'dominant' | 'vivid' | 'soft' | 'contrast'.
 * (Kept for callers that want a single palette; the dialog uses extractPaletteCandidates + paletteVariants.)
 */
export const extractPalette = async (src, { count = 5, mode = 'dominant' } = {}) => {
  const variants = paletteVariants(await extractPaletteCandidates(src), count);
  return (variants.find((v) => v.id === mode) || variants[0] || { colors: [] }).colors;
};

/**
 * Publisher-style 8-colour scheme from up to 5 sampled colours: Black (Text 1), a dark shade of the leading colour (Text 2),
 * the five samples as Accent 1–5 (padded with tints / shades when fewer were found) and White (Background 1).
 */
export const schemeFromPalette = (colors, name = 'From picture') => {
  const accents = colors.map((c) => (typeof c === 'string' ? c : c.hex)).filter(Boolean).slice(0, 5);
  if (!accents.length) return null;
  while (accents.length < 5) {
    const base = accents[accents.length % 2 === 0 ? 0 : accents.length - 1];
    accents.push(accents.length % 2 === 0 ? tint(base, 0.4) : shade(base, 0.35));
  }
  const darkest = accents.slice().sort((a, b) => hexToHsl(a).l - hexToHsl(b).l)[0];
  const text2 = hexToHsl(darkest).l < 30 ? darkest : shade(darkest, 0.55);
  return {
    name,
    colors: [
      { name: 'Black, Text 1', hex: '#000000' },
      { name: 'Text 2', hex: text2 },
      ...accents.map((hex, i) => ({ name: `Accent ${i + 1}`, hex })),
      { name: 'White, Background 1', hex: '#ffffff' },
    ],
  };
};
